import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import "@/App.css";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Rocket, Users, TrendingUp, MessageSquare, BarChart, CheckCircle2, XCircle, Clock, Sparkles, Target, Zap, Brain, FileText, ExternalLink } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Toaster } from "@/components/ui/toaster";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Landing Page
const LandingPage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/stats`);
      setStats(response.data);
    } catch (e) {
      console.error("Error fetching stats", e);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50">
      {/* Header */}
      <header className="border-b bg-white/70 backdrop-blur-xl shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Rocket className="h-8 w-8 text-green-600" />
            <h1 className="text-2xl font-bold text-gray-900">SKI AXIS</h1>
            <Badge className="bg-green-600">Powered by Nova AI</Badge>
          </div>
          <div className="flex gap-4">
            <Button variant="ghost" onClick={() => navigate("/dashboard")} data-testid="dashboard-link">
              Dashboard
            </Button>
            <Button onClick={() => navigate("/apply")} className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 shadow-lg hover:scale-105 transition-all duration-300" data-testid="apply-now-btn">
              Apply Now
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="container mx-auto px-4 py-20 text-center">
        <div className="max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-green-100 px-4 py-2 rounded-full mb-6">
            <Sparkles className="h-4 w-4 text-green-600" />
            <span className="text-sm font-semibold text-green-900">AI-Powered Startup Accelerator</span>
          </div>
          <h1 className="text-6xl font-bold text-gray-900 mb-6">
            Build the Future with <span className="text-green-600">Nova AI</span>
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            Get AI-powered evaluation, mentorship, and funding to accelerate your startup journey.
            Join the next generation of world-changing companies.
          </p>
          <div className="flex gap-4 justify-center">
            <Button size="lg" onClick={() => navigate("/apply")} className="bg-green-600 hover:bg-green-700 text-lg px-8" data-testid="hero-apply-btn">
              <Rocket className="mr-2 h-5 w-5" />
              Start Your Application
            </Button>
            <Button size="lg" variant="outline" onClick={() => navigate("/chat")} className="text-lg px-8" data-testid="talk-to-nova-btn">
              <Brain className="mr-2 h-5 w-5" />
              Talk to Nova
            </Button>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      {stats && (
        <section className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Card data-testid="stat-card-companies">
              <CardHeader className="pb-2">
                <CardDescription>Total Companies</CardDescription>
                <CardTitle className="text-3xl text-green-600">{stats.total_companies}</CardTitle>
              </CardHeader>
            </Card>
            <Card data-testid="stat-card-applications">
              <CardHeader className="pb-2">
                <CardDescription>Applications</CardDescription>
                <CardTitle className="text-3xl text-green-600">{stats.total_applications}</CardTitle>
              </CardHeader>
            </Card>
            <Card data-testid="stat-card-evaluated">
              <CardHeader className="pb-2">
                <CardDescription>Evaluated</CardDescription>
                <CardTitle className="text-3xl text-green-600">{stats.evaluated_applications}</CardTitle>
              </CardHeader>
            </Card>
            <Card data-testid="stat-card-accepted">
              <CardHeader className="pb-2">
                <CardDescription>Accepted</CardDescription>
                <CardTitle className="text-3xl text-green-600">{stats.accepted_companies}</CardTitle>
              </CardHeader>
            </Card>
          </div>
        </section>
      )}

      {/* Features Section */}
      <section className="container mx-auto px-4 py-20">
        <h2 className="text-4xl font-bold text-center mb-12">Why Choose Our Accelerator?</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <Card className="border-none shadow-lg hover:shadow-xl transition-all duration-300 bg-white/90 backdrop-blur" data-testid="feature-ai-evaluation">
            <CardHeader>
              <Target className="h-12 w-12 text-green-600 mb-4" />
              <CardTitle>AI-Powered Evaluation</CardTitle>
              <CardDescription>
                Nova AI evaluates your startup with the precision of top VCs, providing instant feedback and scoring.
              </CardDescription>
            </CardHeader>
          </Card>
          <Card className="border-none shadow-lg hover:shadow-xl transition-all duration-300 bg-white/90 backdrop-blur" data-testid="feature-mentorship">
            <CardHeader>
              <Brain className="h-12 w-12 text-green-600 mb-4" />
              <CardTitle>24/7 AI Mentorship</CardTitle>
              <CardDescription>
                Get strategic advice anytime from Nova, your AI advisor trained on insights from successful founders.
              </CardDescription>
            </CardHeader>
          </Card>
          <Card className="border-none shadow-lg hover:shadow-xl transition-all duration-300 bg-white/90 backdrop-blur" data-testid="feature-fast-track">
            <CardHeader>
              <Zap className="h-12 w-12 text-green-600 mb-4" />
              <CardTitle>Fast-Track Growth</CardTitle>
              <CardDescription>
                Join elite batches, access resources, and connect with a network of ambitious founders.
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-green-600 text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Build Something Great?</h2>
          <p className="text-xl mb-8 opacity-90">Apply now and let Nova help you turn your vision into reality.</p>
          <Button size="lg" variant="secondary" onClick={() => navigate("/apply")} className="text-lg px-8" data-testid="cta-apply-btn">
            Apply to Accelerator
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="container mx-auto px-4 text-center">
          <p className="text-gray-400">© 2026 SKI AXIS with Nova AI. Building the future, one startup at a time.</p>
        </div>
      </footer>
    </div>
  );
};

// Application Form
const ApplicationForm = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [companyData, setCompanyData] = useState({
    name: "",
    founder_name: "",
    founder_email: "",
    linkedin_url: "",
    website: "",
    tier: "basic"
  });
  const [applicationData, setApplicationData] = useState({
    idea_description: "",
    problem_statement: "",
    solution: "",
    target_market: "",
    team_size: 1,
    traction: "",
    funding_stage: "bootstrap",
    governance_focus: "compliance",
    industry: "technology",
    region: "india"
  });

  const handleCompanySubmit = async (e) => {
    e.preventDefault();
    setStep(2);
  };

  const handleApplicationSubmit = async (e) => {
    e.preventDefault();
    try {
      // Create company
      const companyRes = await axios.post(`${API}/companies`, companyData);
      const company = companyRes.data;

      // Create application
      const appData = { ...applicationData, company_id: company.id };
      const appRes = await axios.post(`${API}/applications`, appData);

      toast({
        title: "Application Submitted!",
        description: "Nova will evaluate your application shortly.",
      });

      // Navigate to dashboard
      setTimeout(() => {
        navigate(`/company/${company.id}`);
      }, 1500);
    } catch (error) {
      console.error("Error submitting application:", error);
      toast({
        title: "Error",
        description: "Failed to submit application. Please try again.",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-white py-12">
      <div className="container mx-auto px-4 max-w-3xl">
        <Button variant="ghost" onClick={() => navigate("/")} className="mb-6" data-testid="back-to-home-btn">
          &larr; Back to Home
        </Button>

        {/* Important Document Banner */}
        <Card className="border-2 border-green-500 bg-gradient-to-r from-green-50 to-emerald-50 mb-6 shadow-lg" data-testid="startup-form-document-card">
          <CardContent className="py-4">
            <div className="flex items-center gap-4">
              <div className="bg-green-600 p-3 rounded-full">
                <FileText className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-green-800 text-lg">Important: Review Startup Application Guidelines</h3>
                <p className="text-green-700 text-sm">Please read our detailed guidelines before submitting your application</p>
              </div>
              <a 
                href="https://docs.google.com/document/d/1JJTp65SSmfxWv7qK8KExokjCyrY0KL9d_PX4kXq1Cj4/edit?usp=sharing" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-all duration-300 hover:scale-105"
                data-testid="startup-form-document-link"
              >
                <span>View Document</span>
                <ExternalLink className="h-4 w-4" />
              </a>
            </div>
          </CardContent>
        </Card>

        <Card className="border-none shadow-lg hover:shadow-xl transition-all duration-300 bg-white/90 backdrop-blur" data-testid="application-form-card">
          <CardHeader>
            <CardTitle className="text-3xl">Apply to Accelerator</CardTitle>
            <CardDescription>Tell us about your startup and let Nova evaluate your potential</CardDescription>
          </CardHeader>
          <CardContent>
            {step === 1 ? (
              <form onSubmit={handleCompanySubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="company-name">Company Name *</Label>
                  <Input
                    id="company-name"
                    data-testid="company-name-input"
                    value={companyData.name}
                    onChange={(e) => setCompanyData({ ...companyData, name: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="founder-name">Founder Name *</Label>
                  <Input
                    id="founder-name"
                    data-testid="founder-name-input"
                    value={companyData.founder_name}
                    onChange={(e) => setCompanyData({ ...companyData, founder_name: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="founder-email">Email *</Label>
                  <Input
                    id="founder-email"
                    type="email"
                    data-testid="founder-email-input"
                    value={companyData.founder_email}
                    onChange={(e) => setCompanyData({ ...companyData, founder_email: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="linkedin-url">LinkedIn Profile URL *</Label>
                  <Input
                    id="linkedin-url"
                    data-testid="linkedin-url-input"
                    value={companyData.linkedin_url}
                    onChange={(e) => setCompanyData({ ...companyData, linkedin_url: e.target.value })}
                    required
                    placeholder="https://linkedin.com/in/yourprofile"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="website">Website</Label>
                  <Input
                    id="website"
                    data-testid="website-input"
                    value={companyData.website}
                    onChange={(e) => setCompanyData({ ...companyData, website: e.target.value })}
                  />
                </div>
                <Button type="submit" className="w-full bg-green-600 hover:bg-green-700" data-testid="continue-to-application-btn">
                  Continue to Application
                </Button>
              </form>
            ) : (
              <form onSubmit={handleApplicationSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="idea">Describe your idea in one sentence *</Label>
                  <Input
                    id="idea"
                    data-testid="idea-input"
                    value={applicationData.idea_description}
                    onChange={(e) => setApplicationData({ ...applicationData, idea_description: e.target.value })}
                    required
                    placeholder="e.g., Airbnb for X, Uber for Y..."
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="problem">What problem are you solving? *</Label>
                  <Textarea
                    id="problem"
                    data-testid="problem-input"
                    value={applicationData.problem_statement}
                    onChange={(e) => setApplicationData({ ...applicationData, problem_statement: e.target.value })}
                    required
                    rows={4}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="solution">What's your solution? *</Label>
                  <Textarea
                    id="solution"
                    data-testid="solution-input"
                    value={applicationData.solution}
                    onChange={(e) => setApplicationData({ ...applicationData, solution: e.target.value })}
                    required
                    rows={4}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="market">Target Market *</Label>
                  <Input
                    id="market"
                    data-testid="market-input"
                    value={applicationData.target_market}
                    onChange={(e) => setApplicationData({ ...applicationData, target_market: e.target.value })}
                    required
                    placeholder="e.g., SMBs in North America"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="team-size">Team Size *</Label>
                    <Input
                      id="team-size"
                      type="number"
                      data-testid="team-size-input"
                      value={applicationData.team_size}
                      onChange={(e) => setApplicationData({ ...applicationData, team_size: parseInt(e.target.value) })}
                      required
                      min={1}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="funding-stage">Funding Stage *</Label>
                    <Select
                      value={applicationData.funding_stage}
                      onValueChange={(value) => setApplicationData({ ...applicationData, funding_stage: value })}
                    >
                      <SelectTrigger data-testid="funding-stage-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bootstrap">Bootstrap</SelectItem>
                        <SelectItem value="pre-seed">Pre-seed</SelectItem>
                        <SelectItem value="seed">Seed</SelectItem>
                        <SelectItem value="series-a">Series A</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="traction">Traction (users, revenue, etc.)</Label>
                  <Textarea
                    id="traction"
                    data-testid="traction-input"
                    value={applicationData.traction}
                    onChange={(e) => setApplicationData({ ...applicationData, traction: e.target.value })}
                    rows={3}
                  />
                </div>
                <div className="flex gap-4">
                  <Button type="button" variant="outline" onClick={() => setStep(1)} data-testid="back-to-company-btn">
                    Back
                  </Button>
                  <Button type="submit" className="flex-1 bg-green-600 hover:bg-green-700" data-testid="submit-application-btn">
                    Submit Application
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ApplicationForm;
export { LandingPage, ApplicationForm };